import { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { CultureCode, CurrencyCode, LanguageCode, constructLanguageCodeIso15897 } from '@shared/hooks/useGeoLocale';
import type { CurrencyCodeType } from './currency.types';

export interface SupportedCurrency
  extends Readonly<{
    code: CurrencyCodeType;
    locale: string;
    culture: CultureCode;
    language: LanguageCode;
    flag: string;
  }> {}

export const SUPPORTED_CURRENCIES: readonly SupportedCurrency[] = [
  {
    code: CurrencyCode.AUD,
    culture: CultureCode.AU,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.AU),
    flag: '🇦🇺',
  },
  {
    code: CurrencyCode.CAD,
    culture: CultureCode.CA,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.CA),
    flag: '🇨🇦',
  },
  {
    code: CurrencyCode.EUR,
    culture: CultureCode.DE,
    language: LanguageCode.de,
    locale: constructLanguageCodeIso15897(LanguageCode.de, CultureCode.DE),
    flag: '🇪🇺',
  },
  {
    code: CurrencyCode.GBP,
    culture: CultureCode.GB,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.GB),
    flag: '🇬🇧',
  },
  {
    code: CurrencyCode.JPY,
    culture: CultureCode.JP,
    language: LanguageCode.ja,
    locale: constructLanguageCodeIso15897(LanguageCode.ja, CultureCode.JP),
    flag: '🇯🇵',
  },
  {
    code: CurrencyCode.USD,
    culture: CultureCode.US,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.US),
    flag: '🇺🇸',
  },
];

export const useSupportedCurrencyOptions = (currencies: ReadonlyArray<SupportedCurrency> = SUPPORTED_CURRENCIES): ReadonlyArray<SupportedCurrency & { name: string }> => {
  const { tq } = useTranslation('currencies');

  return useMemo(() => {
    const translateName = (code: CurrencyCodeType) => {
      switch (code) {
        case 'USD':
          return tq`United States Dollar`;
        case 'EUR':
          return tq`Euro`;
        case 'GBP':
          return tq`Pound Sterling`;
        case 'CAD':
          return tq`Canadian Dollar`;
        case 'AUD':
          return tq`Australian Dollar`;
        case 'JPY':
          return tq`Japanese Yen`;
        case 'NZD':
          return tq`New Zealand Dollar`;
        case 'SGD':
          return tq`Singapore Dollar`;
        default:
          return code;
      }
    };

    return currencies.map(o => ({
      ...o,
      name: translateName(o.code),
    }));
  }, [currencies, tq]);
};

/**
 * Currencies supported for print and digital sales. A subset of SUPPORTED_CURRENCIES (excludes JPY).
 * Canonical source shared across @apps/card and @apps/admin — do not duplicate per-app.
 */
export const PRINT_SUPPORTED_CURRENCIES: ReadonlyArray<SupportedCurrency> = [
  {
    code: CurrencyCode.USD,
    culture: CultureCode.US,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.US),
    flag: '🇺🇸',
  },
  {
    code: CurrencyCode.EUR,
    culture: CultureCode.DE,
    language: LanguageCode.de,
    locale: constructLanguageCodeIso15897(LanguageCode.de, CultureCode.DE),
    flag: '🇩🇪',
  },
  {
    code: CurrencyCode.GBP,
    culture: CultureCode.GB,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.GB),
    flag: '🇬🇧',
  },
  {
    code: CurrencyCode.CAD,
    culture: CultureCode.CA,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.CA),
    flag: '🇨🇦',
  },
  {
    code: CurrencyCode.AUD,
    culture: CultureCode.AU,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.AU),
    flag: '🇦🇺',
  },
  {
    code: CurrencyCode.NZD,
    culture: CultureCode.NZ,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.NZ),
    flag: '🇳🇿',
  },
  {
    code: CurrencyCode.SGD,
    culture: CultureCode.SG,
    language: LanguageCode.en,
    locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.SG),
    flag: '🇸🇬',
  },
];

const PRINT_SUPPORTED_CURRENCY_CODES: ReadonlySet<string> = new Set<string>(PRINT_SUPPORTED_CURRENCIES.map(currency => currency.code));

/**
 * Whether stationery can actually be sold in this currency.
 *
 * Case-SENSITIVE, so the `value is CurrencyCode` narrowing stays true. `card_service`
 * rejects `'eur'` exactly as it rejects `'ZAR'` (both fail with "Cannot return null for
 * non-nullable field CurrencyConversion.currencyCode"), so a guard that accepted any
 * casing would vouch for a value the backend cannot price.
 *
 * Pass user-supplied input through {@link asPrintSupportedCurrency} first — it upper-cases
 * before checking.
 */
export const isPrintSupportedCurrency = (value: unknown): value is CurrencyCode => typeof value === 'string' && PRINT_SUPPORTED_CURRENCY_CODES.has(value);

/**
 * Narrow any currency to one print can actually sell in; unsupported → USD.
 *
 * Must be applied on READ, not on write. Two of the four currency sources — the event
 * locale and `navigator.language` — are resolved inside `findClosestMatchingLocale` and
 * reach stationery without passing through any setter, so a write-side guard cannot see
 * them.
 *
 * Returns `CurrencyCode` rather than `CurrencyCodeType` because cards-app APIs
 * (`useMinorUnitsToPriceString`, `useCurrencyConverter`) type `currency` as the enum, and
 * a string-literal union is not assignable to a string enum.
 *
 * Upper-cases before checking. Three of the four currency rungs already arrive upper-cased
 * (`findClosestMatchingLocale` normalizes the `?currency=` param; the event-locale and
 * browser rungs resolve to `CurrencyCode` members), but the stored `joyLocale` rung does
 * not — `resolveLocaleWithOverlay` returns `partial.currency` raw.
 */
export const asPrintSupportedCurrency = (value: unknown): CurrencyCode => {
  const normalized = typeof value === 'string' ? value.toUpperCase() : value;
  return isPrintSupportedCurrency(normalized) ? normalized : CurrencyCode.USD;
};

export const SUGGESTED_CURRENCIES = ['EUR', 'USD', 'AUD'];

const CURRENCY_MAP = new Map<CurrencyCodeType, SupportedCurrency>(SUPPORTED_CURRENCIES.map(currency => [currency.code, currency]));

export const getCurrencyByCode = (code: CurrencyCodeType): SupportedCurrency | undefined => {
  return CURRENCY_MAP.get(code);
};

export const getLocaleFromCurrency = (currencyCode: CurrencyCodeType) => {
  const currency = getCurrencyByCode(currencyCode);

  if (!currency) {
    return {
      culture: CultureCode.US,
      language: LanguageCode.en,
      currency: 'USD',
      locale: constructLanguageCodeIso15897(LanguageCode.en, CultureCode.US),
    };
  }

  return {
    locale: currency.locale,
    currency: currency.code,
    culture: currency.culture,
    language: currency.language,
  };
};
